// src/components/Admin/Settings/Tax/TaxView.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Edit2,
  Trash2,
  Percent,
  Calendar,
  Globe2,
  MapPin,
  CheckCircle,
  XCircle,
  Star,
} from "lucide-react";
import toast from "react-hot-toast";
import TaxAddEditForm from "./TaxAddEditForm";
import useTax from "@/hooks/useTax";
import { ConfirmDialog } from "../../Menu_Management/UIComponents";

export default function TaxManagement({ renderActions }) {
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [filterText, setFilterText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all, active, inactive
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    type: "",
    data: null,
  });

  const showToast = (message, type = "success") => {
    if (type === "success") toast.success(message);
    else if (type === "error") toast.error(message);
    else toast(message);
  };

  const { taxes, createTax, updateTax, deleteTax, fetchTaxes, loading } = useTax();

  // Derive branches from taxes data
  const branches = useMemo(() => {
    const map = new Map();
    taxes.forEach(tax => {
      if (tax.branchId && !map.has(tax.branchId)) {
        map.set(tax.branchId, { id: tax.branchId, name: tax.branchName });
      }
    });
    const list = Array.from(map.values());
    return list.length > 1 ? list : [];
  }, [taxes]);

  const [selectedBranchId, setSelectedBranchId] = useState(null);

  // Auto-select first branch when branches change
  useEffect(() => {
    if (branches.length > 1 && !selectedBranchId) {
      setSelectedBranchId(branches[0].id);
    }
    if (branches.length <= 1) {
      setSelectedBranchId(null);
    }
  }, [branches]);

  // Actions for header
  const actions = {
    onAdd: () => {
      setShowForm(true);
      setEditItem(null);
    },
    onRefresh: fetchTaxes,
  };

  // Call renderActions if provided (for putting buttons in header)
  React.useEffect(() => {
    if (renderActions) {
      renderActions(actions);
    }
  }, [renderActions]);

  const handleSave = async (data) => {
    try {
      if (editItem) {
        await updateTax(editItem.id, data);
        showToast("Tax updated successfully", "success");
      } else {
        await createTax(data);
        showToast("Tax created successfully", "success");
      }
      setShowForm(false);
      setEditItem(null);
    } catch (error) {
      console.error("Failed to save tax:", error);
      showToast("Failed to save tax", "error");
    }
  };


  // Filter taxes based on branch, search text, and status
  const filteredTaxes = useMemo(() => {
    let result = [...taxes];

    // Branch filter
    if (selectedBranchId) {
      result = result.filter(tax => tax.branchId === selectedBranchId);
    }

    // Status filter
    if (statusFilter === "active") {
      result = result.filter(tax => tax.isActive);
    } else if (statusFilter === "inactive") {
      result = result.filter(tax => !tax.isActive);
    }

    // Text search filter
    if (filterText) {
      const text = filterText.toLowerCase();
      result = result.filter(tax =>
        tax.name?.toLowerCase().includes(text) ||
        tax.country?.toLowerCase().includes(text) ||
        tax.state?.toLowerCase().includes(text) ||
        String(tax.rate ?? "").toLowerCase().includes(text) ||
        String(tax.centerGstRate ?? "").toLowerCase().includes(text) ||
        String(tax.stateGstRate ?? "").toLowerCase().includes(text)
      );
    }

    return result;
  }, [taxes, selectedBranchId, statusFilter, filterText]);

  // Sort taxes
  const sortedTaxes = React.useMemo(() => {
    let sortable = [...filteredTaxes];

    if (sortConfig.key !== null) {
      sortable.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (aValue === null || aValue === undefined) aValue = "";
        if (bValue === null || bValue === undefined) bValue = "";

        const aNum = Number(aValue);
        const bNum = Number(bValue);
        const bothNumeric =
          !isNaN(aNum) && !isNaN(bNum) && aValue !== "" && bValue !== "";

        if (bothNumeric) {
          if (aNum < bNum) return sortConfig.direction === "asc" ? -1 : 1;
          if (aNum > bNum) return sortConfig.direction === "asc" ? 1 : -1;
          return 0;
        }

        aValue = aValue.toString().toLowerCase();
        bValue = bValue.toString().toLowerCase();

        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return sortable;
  }, [filteredTaxes, sortConfig]);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown size={14} className="text-gray-400" />;
    }
    return sortConfig.direction === "asc" ? (
      <ArrowUp size={14} className="text-gray-700" />
    ) : (
      <ArrowDown size={14} className="text-gray-700" />
    );
  };

  // Open confirm dialog for delete
  const handleDeleteTax = (id) => {
    setConfirmDialog({
      isOpen: true,
      type: "delete",
      data: id,
    });
  };

  // Actual delete logic
  const confirmDelete = async () => {
    try {
      await deleteTax(confirmDialog.data);
      showToast("Tax deleted", "success");
    } catch (error) {
      console.error("Failed to delete tax:", error);
      showToast("Delete failed", "error");
    } finally {
      setConfirmDialog({ isOpen: false, type: "", data: null });
    }
  };

  // config for ConfirmDialog
  const confirmConfig =
    confirmDialog.type === "delete"
      ? {
          title: "Delete Tax",
          message: "Are you sure you want to delete this tax?",
          confirmText: "Delete",
          type: "danger",
          onConfirm: confirmDelete,
        }
      : null;

  return (
    <div className="min-h-full bg-gray-50 px-4 py-6">
      <div className="space-y-5">
        {/* Branch Tabs */}
        {branches.length > 1 && (
          <div className="flex gap-1 bg-white border border-gray-200 rounded-lg p-1">
            {branches.map(b => (
              <button
                key={b.id}
                onClick={() => {
                  setSelectedBranchId(b.id);
                  setFilterText('');
                  setStatusFilter('all');
                }}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  selectedBranchId === b.id
                    ? 'bg-teal-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {b.name}
              </button>
            ))}
          </div>
        )}

        {/* Search Bar & Filters */}
        <div className="flex items-center gap-4">
          <div className="flex-1 max-w-md relative">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              placeholder="Search by Name, Rate, Location..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th
                    onClick={() => handleSort("name")}
                    className="text-left px-4 py-3 text-xs font-bold text-gray-700 uppercase tracking-wide cursor-pointer hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Name
                      {getSortIcon("name")}
                    </div>
                  </th>

                  <th
                    onClick={() => handleSort("centerGstRate")}
                    className="text-left px-4 py-3 text-xs font-bold text-gray-700 uppercase tracking-wide cursor-pointer hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      CGST
                      {getSortIcon("centerGstRate")}
                    </div>
                  </th>

                  <th
                    onClick={() => handleSort("stateGstRate")}
                    className="text-left px-4 py-3 text-xs font-bold text-gray-700 uppercase tracking-wide cursor-pointer hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      SGST
                      {getSortIcon("stateGstRate")}
                    </div>
                  </th>

                  <th
                    onClick={() => handleSort("country")}
                    className="text-left px-4 py-3 text-xs font-bold text-gray-700 uppercase tracking-wide cursor-pointer hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Location
                      {getSortIcon("country")}
                    </div>
                  </th>

                  <th
                    onClick={() => handleSort("isActive")}
                    className="text-left px-4 py-3 text-xs font-bold text-gray-700 uppercase tracking-wide cursor-pointer hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Status
                      {getSortIcon("isActive")}
                    </div>
                  </th>

                  <th className="text-center px-4 py-3 text-xs font-bold text-gray-700 uppercase tracking-wide">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td className="px-4 py-12 text-center text-gray-500" colSpan={6}>
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-10 h-10 border-4 border-gray-200 border-t-teal-600 rounded-full animate-spin"></div>
                        <p className="text-sm font-medium">Loading...</p>
                      </div>
                    </td>
                  </tr>
                ) : sortedTaxes.length === 0 ? (
                  <tr>
                    <td className="px-4 py-12 text-center text-gray-500" colSpan={6}>
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                          <Percent className="w-8 h-8 text-gray-400" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">No taxes found</p>
                          <p className="text-sm text-gray-500 mt-1">
                            {filterText ? "Try adjusting your search terms" : "Get started by adding your first tax"}
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  sortedTaxes.map((tax) => (
                    <tr key={tax.id} className="hover:bg-gray-50">
                      {/* Name + Rate */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${tax.isDefault ? 'bg-amber-100' : 'bg-teal-100'}`}>
                            {tax.isDefault ? (
                              <Star className="w-4 h-4 text-amber-600 fill-amber-600" />
                            ) : (
                              <Percent className="w-4 h-4 text-teal-600" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-gray-900">{tax.name}</p>
                              {tax.isDefault && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700">
                                  DEFAULT
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500">
                              Rate: <span className="font-medium">{tax.rate ?? 0}%</span>
                              {tax.baseRate != null && tax.baseRate !== "" && (
                                <span className="ml-1 text-gray-400">(Base: {tax.baseRate}%)</span>
                              )}
                            </p>
                            {tax.startDate && (
                              <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                                <Calendar className="w-3 h-3" />
                                From: {tax.startDate}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* CGST */}
                      <td className="px-4 py-3">
                        {tax.centerGstRate != null && tax.centerGstRate !== "" ? (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                            {tax.centerGstRate}%
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm">—</span>
                        )}
                      </td>

                      {/* SGST */}
                      <td className="px-4 py-3">
                        {tax.stateGstRate != null && tax.stateGstRate !== "" ? (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700">
                            {tax.stateGstRate}%
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm">—</span>
                        )}
                      </td>

                      {/* Location (Country + State) */}
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-0.5">
                          {tax.country ? (
                            <div className="inline-flex items-center gap-1.5 text-sm text-gray-700">
                              <Globe2 className="w-3.5 h-3.5 text-gray-400" />
                              {tax.country}
                            </div>
                          ) : null}
                          {tax.state ? (
                            <div className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                              <MapPin className="w-3 h-3 text-gray-400" />
                              {tax.state}
                            </div>
                          ) : null}
                          {!tax.country && !tax.state && (
                            <span className="text-gray-400 text-sm">—</span>
                          )}
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">
                        {tax.isActive ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700">
                            <CheckCircle className="w-3.5 h-3.5" />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            <XCircle className="w-3.5 h-3.5" />
                            Inactive
                          </span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3 text-center">
                        <div className="inline-flex items-center gap-1">
                          <button
                            onClick={() => {
                              setEditItem(tax);
                              setShowForm(true);
                            }}
                            className="p-1.5 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit2 size={15} />
                          </button>
                          <button
                            onClick={() => handleDeleteTax(tax.id)}
                            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Results count */}
        {!loading && sortedTaxes.length > 0 && (
          <div className="px-2">
            <p className="text-sm text-gray-600">
              Showing{" "}
              <span className="font-semibold text-gray-900">
                {sortedTaxes.length}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-gray-900">
                {taxes.length}
              </span>{" "}
              Taxes
              {filterText && " (filtered)"}
            </p>
          </div>
        )}
      </div>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <TaxAddEditForm
          item={editItem}
          onClose={() => {
            setShowForm(false);
            setEditItem(null);
          }}
          onSave={handleSave}
        />
      )}

      {/* Confirm dialog */}
      {confirmConfig && (
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          onClose={() =>
            setConfirmDialog({ isOpen: false, type: "", data: null })
          }
          {...confirmConfig}
        />
      )}
    </div>
  );
}
